import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ROUTE_NAMES } from "../../../routes/routeConstants";
import { AcaIcon } from "../aca";
import { VELLUM as T, SANS, display, mono, body, btn } from "../folio/kit";

/* The general note, stripped along the foot of the sheet.

   A drawing carries its notes at the bottom edge, under a heavy rule, in the
   same ink as everything else - it does not float a card over the work. That
   suits this notice: it says the site sets one essential cookie, no
   tracking, nothing to opt out of. A shield badge and a pair of pill buttons
   dressed a modest statement as a consent negotiation it is not.

   One authored moment: the note rises from the foot it belongs to. */

const P = "ck";
const COOKIE_CONSENT_KEY = "acanetic-cookie-consent";
const TITLE_ID = "cookie-notice-title";

const CookieConsentBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(
      COOKIE_CONSENT_KEY,
      JSON.stringify({ accepted: true, timestamp: new Date().toISOString() })
    );
    setIsVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(
      COOKIE_CONSENT_KEY,
      JSON.stringify({ accepted: false, timestamp: new Date().toISOString() })
    );
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <aside
      aria-labelledby={TITLE_ID}
      className={`${P}-note`}
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        /* Under the auth dialog, over the page. */
        zIndex: 90,
        background: T.ground,
        borderTop: `1.5px solid ${T.ink}`,
        color: T.ink,
        fontFamily: SANS,
      }}
    >
      <div
        className={`${P}-grid`}
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          padding: "22px 20px 24px",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 1px 296px",
          columnGap: 40,
          alignItems: "start",
        }}
      >
        <div style={{ gridColumn: 1, gridRow: 1 }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 20, flexWrap: "wrap" }}>
            <h2 id={TITLE_ID} style={display(T, "21px")}>
              Cookie Notice
            </h2>
            <span style={mono(T, T.accent)}>§ Note · cookies</span>
          </div>
          <p style={{ ...body(T), fontSize: 14.5, lineHeight: 1.55, marginTop: 10, maxWidth: "78ch" }}>
            We use a single essential cookie for authentication to keep you logged in securely. This
            cookie is strictly necessary for the platform to function and cannot be disabled. We do
            not use any tracking or marketing cookies. Read more in our{" "}
            <Link to={ROUTE_NAMES.PAGES.PRIVACY} className={`${P}-link`} style={{ color: T.accent, textDecoration: "underline", textUnderlineOffset: 3 }}>
              Privacy Policy
            </Link>
            .
          </p>
        </div>

        <div aria-hidden className={`${P}-rule`} style={{ gridColumn: 2, gridRow: 1, alignSelf: "stretch", width: 1, background: T.rule }} />

        {/* The actions sit in the margin, on the same 296px track the rest of
            the site annotates in. */}
        <div className={`${P}-actions`} style={{ gridColumn: 3, gridRow: 1, display: "grid", gap: 8 }}>
          <button type="button" onClick={handleAccept} className={`${P}-accent`} style={{ ...btn(T, "accent"), justifyContent: "center" }}>
            Accept
          </button>
          <button type="button" onClick={handleReject} className={`${P}-out`} style={{ ...btn(T, "outline"), justifyContent: "center" }}>
            Essential Only
          </button>
        </div>
      </div>

      {/* Same action as Essential Only, and now it says so out loud: the
          icon-only button had no accessible name at all. */}
      <button
        type="button"
        onClick={handleReject}
        aria-label="Dismiss, essential cookies only"
        className={`${P}-close`}
        style={{
          position: "absolute",
          top: 10,
          right: 12,
          width: 28,
          height: 28,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: T.mute,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AcaIcon name="close" size={15} />
      </button>

      <style>{`
        .${P}-accent { transition: background 150ms ease; }
        .${P}-accent:hover { background: ${T.accentDeep}; }
        .${P}-out { transition: background 150ms ease, color 150ms ease; }
        .${P}-out:hover { background: ${T.ink}; color: ${T.ground}; }
        .${P}-link:hover { color: ${T.accentDeep}; }
        .${P}-close:hover { color: ${T.ink}; }
        .${P}-accent:focus-visible,
        .${P}-out:focus-visible,
        .${P}-link:focus-visible,
        .${P}-close:focus-visible {
          outline: 2px solid ${T.accent};
          outline-offset: 3px;
        }

        @media (prefers-reduced-motion: no-preference) {
          @keyframes ${P}-rise { from { transform: translateY(100%); } to { transform: translateY(0); } }
          .${P}-note { animation: ${P}-rise 320ms cubic-bezier(0.16, 1, 0.3, 1) both; }
        }
        @media (prefers-reduced-motion: reduce) {
          .${P}-accent, .${P}-out { transition: none; }
        }

        @media (max-width: 900px) {
          .${P}-grid { grid-template-columns: minmax(0, 1fr) !important; row-gap: 18px; }
          .${P}-rule { display: none; }
          .${P}-actions { grid-column: 1 !important; grid-row: 2 !important; grid-auto-flow: column; }
        }
        @media (max-width: 480px) {
          .${P}-actions { grid-auto-flow: row; }
        }
      `}</style>
    </aside>
  );
};

export default CookieConsentBanner;
